// Chat log + input box. `addChat` appends a line (text set via textContent so
// player names/messages can't inject markup); `openChat` reveals the input;
// `initChat` wires Enter-to-send / Esc-to-cancel. Sending goes through net.
//
// EMOTE COMMANDS live here, client-side: a leading-`/` line that matches the
// emote table becomes a `pose` intent and never reaches the server's chat
// handler (whose `/` parsing is DEV_COMMANDS-gated — emotes are a prod
// feature). Anything else `/`-prefixed still goes through as chat so the dev
// `/map` + `/refish` commands keep working.

import { $ } from "./dom.ts";
import { game } from "./state.ts";
import { sendChat, sendPose } from "./net.ts";
import { emotePoseIds } from "./anim/clips.ts";

/** Command word → pose id (null = stand). Every emote-able pose (non-cinematic,
 *  non-seatable — see emotePoseIds) is also reachable by its own id
 *  (/all-fours, /kneeling), so a new pose is emote-able with zero edits here;
 *  the aliases are just the natural words. */
function emotePose(word: string): string | null | undefined {
  const aliases: Record<string, string | null> = {
    sit: "sitting", kneel: "kneeling", allfours: "all-fours", lie: "prone", stand: null,
  };
  if (word in aliases) return aliases[word];
  return emotePoseIds().includes(word) ? word : undefined;
}

/** Intercept an emote command; true = the line was handled (don't send chat).
 *  Re-issuing the pose you already hold stands you back up (toggle). */
function tryEmote(text: string): boolean {
  if (!text.startsWith("/")) return false;
  const pose = emotePose(text.slice(1).trim().toLowerCase());
  if (pose === undefined) return false; // not an emote — let the server see it
  sendPose(pose !== null && game.pose === pose ? null : pose);
  return true;
}

export function addChat(name: string, text: string) {
  const chatlog = $("#chatlog");
  const line = document.createElement("div");
  line.className = name === "system" ? "line system" : "line";
  line.innerHTML = "<b></b> ";
  (line.firstChild as HTMLElement).textContent = name + ":";
  line.appendChild(document.createTextNode(" " + text));
  chatlog.appendChild(line);
  while (chatlog.children.length > 8) chatlog.removeChild(chatlog.firstChild!);
}

export function openChat() {
  if (!game.joined) return;
  const chatInput = $<HTMLInputElement>("#chatinput");
  chatInput.classList.add("open");
  chatInput.focus();
}

export function initChat() {
  const chatInput = $<HTMLInputElement>("#chatinput");
  chatInput.addEventListener("keydown", (e) => {
    e.stopPropagation(); // don't leak movement keys to the window handler
    if (e.key === "Enter") {
      const text = chatInput.value.trim();
      if (text && !tryEmote(text)) sendChat(text);
      chatInput.value = "";
      chatInput.blur();
      chatInput.classList.remove("open");
    } else if (e.key === "Escape") {
      chatInput.value = "";
      chatInput.blur();
      chatInput.classList.remove("open");
    }
  });
}

// Is the chat box currently focused? (Movement keys are suppressed while typing.)
export const chatTyping = () => document.activeElement === $("#chatinput");
